import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';

const CurrentlyParked = () => {
  const [activeRecords, setActiveRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadActiveRecords();

    // Refresh every 30 seconds to update time remaining
    const interval = setInterval(() => {
      setCurrentTime(new Date());
      loadActiveRecords();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadActiveRecords = async () => {
    setLoading(true);
    try {
      // Get all parking records from the last 24 hours
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const recordsRef = collection(db, 'parkingRecords');
      const q = query(
        recordsRef,
        where('timestamp', '>=', yesterday),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const records = [];

      querySnapshot.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };

        // Calculate if parking is still active
        if (data.timestamp && data.hours) {
          const startTime = data.timestamp.toDate();
          const durationMs = data.hours * 60 * 60 * 1000; // Convert hours to milliseconds
          const endTime = new Date(startTime.getTime() + durationMs);

          // Only include if parking time hasn't expired
          if (endTime > now) {
            data.startTime = startTime;
            data.endTime = endTime;
            records.push(data);
          }
        }
      });

      setActiveRecords(records);
    } catch (error) {
      console.error('Error loading active records:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeRemaining = (endTime) => {
    const now = currentTime;
    const diffMs = endTime - now;

    if (diffMs <= 0) {
      return { text: 'Expired', minutes: 0, status: 'expired' };
    }

    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    let status = 'active';
    if (minutes <= 15) {
      status = 'expiring-soon';
    } else if (minutes <= 30) {
      status = 'warning';
    }

    const text = hours > 0
      ? `${hours}h ${mins}m`
      : `${mins}m`;

    return { text, minutes, status };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'expiring-soon':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading active parking sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Summary */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3">
              <h2 className="text-2xl font-bold text-gray-900">Currently Parked Vehicles</h2>
              <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-md">Live Square Data</span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Last updated: {currentTime.toLocaleTimeString()}
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold text-blue-600">{activeRecords.length}</div>
            <div className="text-sm text-gray-600 uppercase font-medium">Active</div>
          </div>
        </div>
      </div>

      {/* Active Records Grid */}
      {activeRecords.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No Active Parking Sessions</h3>
          <p className="mt-2 text-sm text-gray-500">
            All parking spots are currently available
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {activeRecords.map((record) => {
            const timeRemaining = getTimeRemaining(record.endTime);

            return (
              <div
                key={record.id}
                className={`bg-white rounded-lg shadow border-l-4 ${
                  timeRemaining.status === 'active' ? 'border-green-500' :
                  timeRemaining.status === 'warning' ? 'border-yellow-500' :
                  timeRemaining.status === 'expiring-soon' ? 'border-orange-500' :
                  'border-red-500'
                } p-6 hover:shadow-lg transition-shadow`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">{record.name}</h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(timeRemaining.status)}`}>
                        {timeRemaining.text} left
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="font-mono font-semibold">{record.licensePlate}</span>
                      </div>

                      {record.spotId && (
                        <div className="flex items-center text-sm text-gray-600">
                          <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>Spot: <strong>{record.spotId}</strong></span>
                        </div>
                      )}

                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Duration: <strong>{record.duration || `${record.hours}h`}</strong></span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="h-5 w-5 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Paid: <strong className="text-green-600">${record.amount?.toFixed(2)}</strong></span>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
                        <div>
                          <span className="block font-medium text-gray-700">Check-in</span>
                          <span>{formatTime(record.startTime)}</span>
                          <span className="block">{formatDate(record.startTime)}</span>
                        </div>
                        <div>
                          <span className="block font-medium text-gray-700">Expires</span>
                          <span>{formatTime(record.endTime)}</span>
                          <span className="block">{formatDate(record.endTime)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        timeRemaining.status === 'active' ? 'bg-green-500' :
                        timeRemaining.status === 'warning' ? 'bg-yellow-500' :
                        timeRemaining.status === 'expiring-soon' ? 'bg-orange-500' :
                        'bg-red-500'
                      }`}
                      style={{
                        width: `${Math.max(0, Math.min(100, (timeRemaining.minutes / (record.hours * 60)) * 100))}%`
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {activeRecords.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Status Legend</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Active (&gt;30 min)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Warning (15-30 min)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Expiring Soon (&lt;15 min)</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <span className="text-gray-600">Expired</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentlyParked;
